import { createColumnHelper } from '@tanstack/react-table';
import type { CellContext } from '@tanstack/react-table';
import { AlertCircle, Info, Loader2Icon } from 'lucide-react';
import { unparse } from 'papaparse';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { Button } from '~shared/shadcn/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { type ClassData, getDragFillErrors, validateCell } from './validation';

export interface ValidateCSVProps {
  csvData: Array<ClassData>;
  embedded?: boolean;
  setStep?: React.Dispatch<
    React.SetStateAction<'select' | 'mapping' | 'validate'>
  >;
  columnHeaders?: Array<string>;
  handleUpload?: () => Promise<void>;
  isUploading?: boolean;
  hourlyPlanNames?: Array<string>;
}

// const columnHelper = createColumnHelper<Record<CSVHeader, string>>();
const columnHelper = createColumnHelper<ClassData>();

// Reusable component for fee fields that should be ignored for hourly billing
const ConditionalValidationCell = ({
  info,
  fieldName,
}: {
  info: CellContext<ClassData, unknown>;
  fieldName: keyof ClassData;
}) => {
  const value = info.getValue();
  const isHourly = info.row.original['Tuition Billing Cycle'] === 'Hourly';
  // Pass context for cross-field validation (needed for Tuition Fee and Billing Day)
  const needsContext =
    fieldName === 'Tuition Fee' || fieldName === 'Billing Day';
  const validation = validateCell(
    fieldName,
    value,
    needsContext ? info.row.original : isHourly ? info.row.original : undefined,
  );
  const warningMessage = isHourly
    ? 'This field will be ignored for hourly billing'
    : validation.errorMessage;

  // Convert value to string safely
  const displayValue =
    value == null
      ? ''
      : typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : '';

  return (
    <div
      className={`p-2 ${!validation.isValid ? 'bg-red-200' : isHourly ? 'bg-yellow-100' : ''}`}
      title={warningMessage}
    >
      {displayValue}
    </div>
  );
};

const createColumns = (
  hourlyPlanNames?: Array<string>,
  sessionDragFillErrors?: Map<number, string>,
  groupDragFillErrors?: Map<number, string>,
  locationDragFillErrors?: Map<number, string>,
) => [
  columnHelper.accessor('Title', {
    header: (info) => (
      <DataTableColumnHeader
        title='Title'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Title', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Class Color', {
    header: (info) => (
      <DataTableColumnHeader
        title='Class Color'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Class Color', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Hide Class Status', {
    header: (info) => (
      <DataTableColumnHeader
        title='Hide Class Status'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Hide Class Status', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Location', {
    header: (info) => (
      <DataTableColumnHeader
        title='Location'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Location', value);
      const dragFillError = locationDragFillErrors?.get(info.row.index);
      return (
        <div
          className={`p-2 ${!validation.isValid || dragFillError ? 'bg-red-200' : ''}`}
          title={validation.errorMessage ?? dragFillError}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Room', {
    header: (info) => (
      <DataTableColumnHeader
        title='Room'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Room', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('start age year', {
    header: (info) => (
      <DataTableColumnHeader
        title='Start Age (Years)'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('start age year', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('start age month', {
    header: (info) => (
      <DataTableColumnHeader
        title='Start Age Month (1-12)'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('start age month', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('end age year', {
    header: (info) => (
      <DataTableColumnHeader
        title='End Age (Years)'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('end age year', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('end age month', {
    header: (info) => (
      <DataTableColumnHeader
        title='End Age Month (1-12)'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('end age month', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Instructor', {
    header: (info) => (
      <DataTableColumnHeader
        title='Instructor'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Instructor', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Description (Under 200 Characters)', {
    header: (info) => (
      <DataTableColumnHeader
        title='Description (Under 200 Characters)'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell(
        'Description (Under 200 Characters)',
        value,
      );
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Session', {
    header: (info) => (
      <DataTableColumnHeader
        title='Session'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Session', value);
      const dragFillError = sessionDragFillErrors?.get(info.row.index);
      return (
        <div
          className={`p-2 ${!validation.isValid || dragFillError ? 'bg-red-200' : ''}`}
          title={validation.errorMessage ?? dragFillError}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Start Date', {
    header: (info) => (
      <DataTableColumnHeader
        title='Start Date'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Start Date', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('End Date', {
    header: (info) => (
      <DataTableColumnHeader
        title='End Date'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('End Date', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Registration Start Date', {
    header: (info) => (
      <DataTableColumnHeader
        title='Registration Start Date'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Registration Start Date', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Days', {
    header: (info) => (
      <DataTableColumnHeader
        title='Days'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Days', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Start Time', {
    header: (info) => (
      <DataTableColumnHeader
        title='Start Time'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Start Time', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('End Time', {
    header: (info) => (
      <DataTableColumnHeader
        title='End Time'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('End Time', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Registration Fee', {
    header: (info) => (
      <DataTableColumnHeader
        title='Registration Fee'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <ConditionalValidationCell info={info} fieldName='Registration Fee' />
    ),
  }),
  columnHelper.accessor('Tuition Fee', {
    header: (info) => (
      <DataTableColumnHeader
        title='Tuition Fee'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <ConditionalValidationCell info={info} fieldName='Tuition Fee' />
    ),
  }),
  columnHelper.accessor('Costume Fee', {
    header: (info) => (
      <DataTableColumnHeader
        title='Costume Fee'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <ConditionalValidationCell info={info} fieldName='Costume Fee' />
    ),
  }),
  columnHelper.accessor('Class Size', {
    header: (info) => (
      <DataTableColumnHeader
        title='Class Size'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Class Size', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Group', {
    header: (info) => (
      <DataTableColumnHeader
        title='Group'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Group', value);
      const dragFillError = groupDragFillErrors?.get(info.row.index);
      return (
        <div
          className={`p-2 ${!validation.isValid || dragFillError ? 'bg-red-200' : ''}`}
          title={validation.errorMessage ?? dragFillError}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Tuition Billing Cycle', {
    header: (info) => (
      <DataTableColumnHeader
        title='Tuition Billing Cycle'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Tuition Billing Cycle', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Billing Frequency Days', {
    header: (info) => (
      <DataTableColumnHeader
        title='Billing Frequency Days'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const isEveryXDays =
        info.row.original['Tuition Billing Cycle'] === 'every-x-days';
      const validation = validateCell(
        'Billing Frequency Days',
        value,
        info.row.original,
      );
      const hasWarning = !isEveryXDays && value;
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : hasWarning ? 'bg-yellow-100' : ''}`}
          title={
            !validation.isValid
              ? validation.errorMessage
              : hasWarning
                ? 'This field will be ignored unless billing cycle is Every X Days'
                : undefined
          }
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Hourly Plan Name', {
    header: (info) => (
      <DataTableColumnHeader
        title='Hourly Plan Name'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const isHourly = info.row.original['Tuition Billing Cycle'] === 'Hourly';
      const validation = validateCell(
        'Hourly Plan Name',
        value,
        info.row.original,
        hourlyPlanNames,
      );
      const hasError = !validation.isValid && isHourly;
      const hasValidPlans = hourlyPlanNames && hourlyPlanNames.length > 0;

      return (
        <div
          className={`flex items-center gap-2 p-2 ${hasError ? 'bg-red-200' : ''}`}
        >
          <span>{value}</span>
          {hasError && hasValidPlans && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertCircle className='size-4 cursor-help text-red-600' />
              </TooltipTrigger>
              <TooltipContent className='max-w-md'>
                <div className='space-y-2'>
                  <div className='text-sm font-semibold'>
                    Available Hourly Plan Names:
                  </div>
                  <div className='text-sm'>{hourlyPlanNames.join(', ')}</div>
                  <div className='border-t pt-1 text-xs text-muted-foreground'>
                    Note: Plan names are case-sensitive and must match exactly.
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('Billing Day', {
    header: (info) => (
      <DataTableColumnHeader
        title='Billing Day'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => (
      <ConditionalValidationCell info={info} fieldName='Billing Day' />
    ),
  }),
];

export const ValidateCSV = ({
  csvData,
  embedded = false,
  setStep,
  handleUpload,
  isUploading = false,
  hourlyPlanNames,
}: ValidateCSVProps) => {
  const sessionDragFillErrors = getDragFillErrors(csvData, 'Session');
  const groupDragFillErrors = getDragFillErrors(csvData, 'Group');
  const locationDragFillErrors = getDragFillErrors(csvData, 'Location');

  const columns = createColumns(
    hourlyPlanNames,
    sessionDragFillErrors,
    groupDragFillErrors,
    locationDragFillErrors,
  );
  const handleDownloadCSV = () => {
    const csvDataNew = csvData.map((row) => {
      const errors: Array<string> = [];
      const newRow: Record<string, string | number | undefined> = { ...row };
      Object.keys(row).forEach((key) => {
        const validation = validateCell(
          key as keyof ClassData,
          row[key as keyof ClassData],
          row,
          hourlyPlanNames,
        );
        if (!validation.isValid) {
          newRow[key as keyof ClassData] =
            `[INVALID: ${row[key as keyof ClassData]}]`;
          errors.push(`${key}: ${validation.errorMessage}`);
        }
      });
      newRow['Validation Errors'] = errors.join('; ');
      return newRow;
    });

    const csv = unparse(csvDataNew);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'class_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isCSVDataValid = csvData.every((row, index) => {
    const cellValid = Object.keys(row).every((key) => {
      const validation = validateCell(
        key as keyof ClassData,
        row[key as keyof ClassData],
        row,
        hourlyPlanNames,
      );
      return validation.isValid;
    });
    return (
      cellValid &&
      !sessionDragFillErrors.has(index) &&
      !groupDragFillErrors.has(index) &&
      !locationDragFillErrors.has(index)
    );
  });

  const hasHourlyRows = csvData.some(
    (row) => row['Tuition Billing Cycle'] === 'Hourly',
  );

  const dragFillColumns = [
    sessionDragFillErrors.size > 0 ? 'Session' : '',
    groupDragFillErrors.size > 0 ? 'Group' : '',
    locationDragFillErrors.size > 0 ? 'Location' : '',
  ].filter(Boolean);
  const hasDragFillErrors = dragFillColumns.length > 0;

  return (
    <div>
      {hasHourlyRows && hourlyPlanNames && hourlyPlanNames.length > 0 && (
        <div className='mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3'>
          <div className='flex items-start gap-2'>
            <Info className='mt-0.5 size-4 text-blue-600' />
            <div className='flex-1'>
              <div className='text-sm font-medium text-blue-900'>
                Available Hourly Plan Names:
              </div>
              <div className='mt-1 text-sm text-blue-700'>
                {hourlyPlanNames.join(', ')}
              </div>
              <div className='mt-2 text-xs text-blue-600'>
                Note: Plan names are case-sensitive and must match exactly.
              </div>
            </div>
          </div>
        </div>
      )}
      {hasHourlyRows && (!hourlyPlanNames || hourlyPlanNames.length === 0) && (
        <div className='mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='mt-0.5 size-4 text-yellow-600' />
            <div className='flex-1'>
              <div className='text-sm font-medium text-yellow-900'>
                No hourly plans found
              </div>
              <div className='mt-1 text-sm text-yellow-700'>
                Please create hourly tuition billing plans before uploading
                hourly enrollments.
              </div>
            </div>
          </div>
        </div>
      )}
      {hasDragFillErrors && (
        <div className='mb-4 rounded-lg border border-red-200 bg-red-50 p-3'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='mt-0.5 size-4 text-red-600' />
            <div className='flex-1'>
              <div className='text-sm font-medium text-red-900'>
                Possible auto-filled values detected in{' '}
                {dragFillColumns.join(', ')}
              </div>
              <div className='mt-1 text-sm text-red-700'>
                Spreadsheet fill handles can auto-increment numbers in cells
                (for example, turning &quot;Session 2026&quot; into
                &quot;Session 2027&quot;, &quot;Session 2028&quot;, etc.). Copy
                and paste the exact value for each row instead of dragging the
                cell handle. Correct the values below before uploading.
              </div>
            </div>
          </div>
        </div>
      )}
      <EnrollioTable
        ariaLabel='Class CSV validation'
        totalLabel='CSV Rows'
        filtersLayout='inline'
        filterHeader={
          <span className='text-sm text-muted-foreground'>
            {isCSVDataValid
              ? 'All rows are ready to upload'
              : 'Review highlighted validation errors'}
          </span>
        }
        toolbar={(table) => (
          <>
            <EnrollioTableColumnToggle table={table} />
            {embedded && isCSVDataValid ? null : (
              <Button variant='secondary' onClick={handleDownloadCSV}>
                Download CSV
              </Button>
            )}
          </>
        )}
        data={csvData}
        columns={columns}
      />
      {!embedded && setStep && handleUpload && (
        <div className='mt-4 flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <Button
              variant={'outline'}
              onClick={() => {
                setStep('mapping');
              }}
            >
              Back
            </Button>
            <Button
              disabled={!isCSVDataValid || isUploading}
              onClick={handleUpload}
            >
              {isUploading ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Upload'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidateCSV;
